import { createCategoryBodySchema, type Category } from '@klopotun/contracts';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useSaveCategoryMutation } from '../../../../entities/category/index.js';
import { CATEGORY_EMOJIS } from '../../../../shared/config/category-emojis.js';
import { Button, EmojiField, TextField } from '../../../../shared/ui/index.js';
import styles from './new-category-panel.module.css';

type Complaints = {
  name?: string;
  emoji?: string;
};

const COMPLAINT_BY_FIELD: Record<string, Complaints> = {
  name: { name: 'Вкажіть назву категорії' },
  emoji: { emoji: 'Емодзі не підходить' },
};

const complaintsOf = (paths: PropertyKey[][]): Complaints =>
  paths.reduce<Complaints>((complaints, path) => {
    const [firstSegment] = path;
    const complaint =
      typeof firstSegment === 'string' ? COMPLAINT_BY_FIELD[firstSegment] : undefined;

    return { ...complaints, ...complaint };
  }, {});

const nextSortOrder = (categories: Category[]): number =>
  categories.length === 0 ? 0 : Math.max(...categories.map((item) => item.sortOrder)) + 1;

type NewCategoryPanelProps = {
  categories: Category[];
  onCreated: (category: Category) => void;
  onCancel: () => void;
};

/**
 * A name and an emoji without leaving the subscription form; the colour waits on the references
 * screen. Not a form element on purpose - it sits inside the subscription form, and forms do not
 * nest.
 */
export const NewCategoryPanel = ({ categories, onCreated, onCancel }: NewCategoryPanelProps) => {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [complaints, setComplaints] = useState<Complaints>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const saveCategory = useSaveCategoryMutation();

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = (): void => {
    const checked = createCategoryBodySchema.safeParse({
      name: name.trim(),
      emoji: emoji.trim() === '' ? null : emoji.trim(),
      color: null,
      sortOrder: nextSortOrder(categories),
    });

    if (!checked.success) {
      setComplaints(complaintsOf(checked.error.issues.map((issue) => issue.path)));
      return;
    }

    setComplaints({});
    saveCategory.mutate({ mode: 'create', body: checked.data }, { onSuccess: onCreated });
  };

  // Enter inside these inputs would otherwise submit the surrounding subscription form.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    submit();
  };

  return (
    <div
      className={styles.panel}
      role="group"
      aria-label="Нова категорія"
      onKeyDown={handleKeyDown}
    >
      <p className={styles.title}>Нова категорія</p>

      <TextField
        label="Назва"
        name="newCategoryName"
        value={name}
        ref={nameRef}
        placeholder="Робота"
        error={complaints.name}
        onChange={(event) => {
          setName(event.target.value);
        }}
      />

      <EmojiField
        label="Емодзі"
        name="newCategoryEmoji"
        value={emoji}
        presets={CATEGORY_EMOJIS}
        hint="Не обов'язково"
        error={complaints.emoji}
        onChange={setEmoji}
      />

      {saveCategory.error !== null && (
        <p className={styles.actionError} role="alert">
          {saveCategory.error.message}
        </p>
      )}

      <div className={styles.actions}>
        <Button busy={saveCategory.isPending} onClick={submit}>
          Додати категорію
        </Button>
        <Button variant="secondary" disabled={saveCategory.isPending} onClick={onCancel}>
          Скасувати
        </Button>
      </div>
    </div>
  );
};
